import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { IDSBadge, IDSBreadcrumbs } from "@inera/ids-react";
import { fetchContractsForOrg } from "../../../services/contractsAPI";
import { useAuth } from "../../../contexts/AuthContext";
import { useOrganisation } from "../../../contexts/OrganisationContext";
import { useContract } from "../../../contexts/ContractContext";
import ModeSelectionScreen from "../logical/ModeSelectionScreen";
import TestclientLayout from "./TestclientLayout";


const MODE_LABELS = {
  standard: { label: "Testrapport",        type: "info"      },
  sandbox:  { label: "Labbmiljö",          type: "attention" },
  manual:   { label: "Självdeklaration", type: "info"      },
};

/**
 * Test execution view for a specific service contract.
 * Shows a mode picker (standard / sandbox / manual) before the test workflow.
 * Reads the contractId from the URL and syncs it into ContractContext.
 */
const TestRun = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { activeContractId, setActiveContractId, activeContractName, setActiveContractName, setActiveContractVersion, activeContractDocUrl, setActiveContractDocUrl } = useContract();
  const { activeOrgId } = useOrganisation();
  const { loading: authLoading, isAuthenticated } = useAuth();

  const [mode, setMode] = useState(null); // null | 'standard' | 'sandbox'
  const [inReview, setInReview] = useState(false);

  // Sync URL param into context (handles direct navigation / page refresh)
  useEffect(() => {
    if (contractId && String(activeContractId) !== String(contractId)) {
      setActiveContractId(Number(contractId));
    }
  }, [contractId]);

  // Fetch contract details to get name + inReview flag when arriving directly via URL
  useEffect(() => {
    if (authLoading || !isAuthenticated || !activeOrgId || !contractId) return;

    // If we already have the name (navigated from ContractSelection) skip the fetch
    if (activeContractName) {
      // Still need inReview — fetch contracts list
      fetchContractsForOrg(activeOrgId).then((contracts) => {
        const found = contracts.find((c) => String(c.id) === String(contractId));
        if (found) {
          setActiveContractName(found.contractName);
          setActiveContractVersion(found.version || null);
          setActiveContractDocUrl(found.documentationUrl || null);
          setInReview(found.inReview);
        }
      }).catch(() => {});
      return;
    }

    fetchContractsForOrg(activeOrgId).then((contracts) => {
      const found = contracts.find((c) => String(c.id) === String(contractId));
      if (found) {
        setActiveContractName(found.contractName);
        setActiveContractVersion(found.version || null);
        setActiveContractDocUrl(found.documentationUrl || null);
        setInReview(found.inReview);
      }
    }).catch(() => {});
  }, [authLoading, isAuthenticated, activeOrgId, contractId]);

  const modeMeta = mode ? MODE_LABELS[mode] : null;

  return (
    <div>
      <main>
        {/* Breadcrumb navigation */}
        <div className="ids-px-6 ids-pt-4 ids-pb-2" style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <IDSBreadcrumbs lead="Du är här:">
            <Link to="/start">Start</Link>
            <Link to="/testklient">Testklient</Link>
            <a>{activeContractName || "Kontrakt"}</a>
          </IDSBreadcrumbs>

          {/* Active mode badge */}
          {modeMeta && (
            <IDSBadge type={modeMeta.type}>{modeMeta.label}</IDSBadge>
          )}
        </div>

        {/* Mode picker — shown until user makes a choice */}
        {!mode ? (
          <ModeSelectionScreen
            contractName={activeContractName || ""}
            documentationUrl={activeContractDocUrl}
            inReview={inReview}
            onSelect={(chosen) => setMode(chosen)}
            onClose={() => navigate("/testklient")}
          />
        ) : (
          <TestclientLayout mode={mode} />
        )}
      </main>
    </div>
  );
};

export default TestRun;
